import React from "react";
import { Box, keyframes } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useAssistantChat } from "./AssistantChatContext";
import { openLoginPopup } from "../../redux/slices/authSlice";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

/**
 * A small AI assistant button to be placed on product cards (top left).
 * Checks authentication before opening the chat popup.
 *
 * @param {object} product - The product object (must have at least url/product_url)
 */
const attentionPulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 0 rgba(255, 140, 56, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5), 0 0 0 6px rgba(255, 140, 56, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 0 rgba(255, 140, 56, 0);
  }
`;

const twinkle = keyframes`
  0%, 100% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1) rotate(180deg); opacity: 1; }
`;

const mainIconSparkle = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); }
  50% { transform: scale(1.15) rotate(15deg); }
`;

const ProductAssistantButton = ({ product, sx = {} }) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { openChat } = useAssistantChat();

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent card click from firing
    e.preventDefault();

    if (!isAuthenticated) {
      dispatch(openLoginPopup());
      return;
    }

    openChat(product);
  };

  return (
    <Box
      onClick={handleClick}
      aria-label="Ask AI about this product"
      sx={{
        position: "absolute",
        top: { xs: 8, sm: 10 },
        left: { xs: 8, sm: 10 },
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: { xs: 32, sm: 36 },
        height: { xs: 32, sm: 36 },
        borderRadius: "50%",
        background: "#FFFFFF",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        border: "1px solid rgba(255, 140, 56, 0.5)",
        color: "#FFF",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `${attentionPulse} 2s infinite`,
        "&:hover": {
          animation: "none",
          background: "#FFF5EE",
          border: "1px solid rgba(255, 140, 56, 0.8)",
          transform: "translateY(-2px) scale(1.02)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.1), 0 0 12px rgba(255, 140, 56, 0.4)",
        },
        "&:active": {
          transform: "scale(0.95)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: -12,
          left: -12,
          right: -12,
          bottom: -12,
        },
        ...sx,
      }}
    >
      <AutoAwesomeIcon 
        sx={{ 
          color: "#FF8C38", 
          fontSize: { xs: "18px", sm: "20px" }, 
          zIndex: 1,
          animation: `${mainIconSparkle} 3s ease-in-out infinite`
        }} 
      />
    </Box>
  );
};

export default ProductAssistantButton;
